import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import es from './locales/es.json';

export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'coreway.locale';

function isSupported(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Picks the language: an explicit choice first, then the runtime config, then the browser.
 *
 * Wired now, with ten strings, because the product is bilingual by requirement — the dispatchers are
 * largely Spanish-speaking and the brokers are not — and retrofitting i18n over a grown portal costs
 * an order of magnitude more than carrying it from the first screen.
 */
export function resolveLocale(configured: string | null): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isSupported(stored)) return stored;
  if (isSupported(configured)) return configured;
  const browser = navigator.language.split('-')[0];
  return isSupported(browser) ? browser : 'en';
}

export function setLocale(locale: Locale): void {
  localStorage.setItem(STORAGE_KEY, locale);
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

/** Extra copy per language, merged on top of the portal files (the operator panel's own locale files). */
export type ExtraTranslations = Partial<Record<Locale, Record<string, unknown>>>;

/**
 * `initI18n(configured)` is the portal. The operator panel (web/admin) passes its own files as `extra`:
 * it reuses the shared components, so it needs the portal copy too, while the portal never carries the
 * panel's. The admin files live under one `operator` namespace the portal files never use (asserted in
 * admin/src/locales/locales.test.ts), so the merge can only add keys, never overwrite portal copy.
 */
export function initI18n(configured: string | null, extra?: ExtraTranslations): typeof i18n {
  const lng = resolveLocale(configured);
  void i18n.use(initReactI18next).init({
    resources: {
      en: { translation: { ...en, ...(extra?.en ?? {}) } },
      es: { translation: { ...es, ...(extra?.es ?? {}) } },
    },
    lng,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });
  document.documentElement.lang = lng;
  return i18n;
}

/** Adds `extra` to an already initialized instance (deep merge, existing keys kept). Tests use it. */
export function addTranslations(extra: ExtraTranslations): void {
  for (const locale of SUPPORTED_LOCALES) {
    const bundle = extra[locale];
    if (bundle) i18n.addResourceBundle(locale, 'translation', bundle, true, false);
  }
}
